<script>
export default {
  name: 'PanjarIkhtisarTable',

  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },

    // Daftar header ikhtisar.
    // Dipakai untuk mencari no_ikhtisar berdasarkan ikhtisar_hd_id.
    ikhtisars: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  methods: {
    // =========================================================
    // FORMAT TANGGAL
    // =========================================================
    formatDate(date) {
      if (!date) return '-'

      try {
        const d = new Date(date)

        if (isNaN(d.getTime())) {
          return '-'
        }

        return d.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch (e) {
        return '-'
      }
    },

    // =========================================================
    // NORMALISASI NOMINAL
    //
    // Semua nominal dianggap Rupiah tanpa pecahan.
    // Mencegah floating point menghasilkan -0.
    // =========================================================
    normalizeNominal(value) {
      const numericValue = Number(value) || 0

      return Math.round(numericValue)
    },

    // =========================================================
    // FORMAT RUPIAH
    // =========================================================
    formatRupiah(value) {
      const roundedValue = this.normalizeNominal(value)

      if (roundedValue === 0) {
        return '0'
      }

      return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    // =========================================================
    // BIAYA LAIN
    // =========================================================
    getBiayaLain(item) {
      if (!item || !Array.isArray(item.biaya_lains)) {
        return 0
      }

      const total = item.biaya_lains.reduce((total, biaya) => {
        return total + Number(biaya.biaya || 0)
      }, 0)

      return this.normalizeNominal(total)
    },

    // =========================================================
    // NILAI DPP
    // =========================================================
    getDpp(item) {
      return this.normalizeNominal(item && item.nilai_dpp)
    },

    // =========================================================
    // NILAI PPN
    //
    // API menyimpan ppn sebagai persentase.
    // =========================================================
    getPpn(item) {
      const dpp = this.getDpp(item)
      const persen = Number(item && item.ppn) || 0

      return this.normalizeNominal((dpp * persen) / 100)
    },

    // =========================================================
    // TOTAL PANJAR
    //
    // DPP + PPN + BIAYA LAIN
    // =========================================================
    getNilaiTotal(item) {
      const total =
        this.getDpp(item) + this.getPpn(item) + this.getBiayaLain(item)

      return this.normalizeNominal(total)
    },

    // =========================================================
    // TOTAL IKHTISAR
    //
    // 1 PANJAR BISA MEMILIKI BANYAK IKHTISAR
    // =========================================================
    getTotalIkhtisar(item) {
      if (!item || !Array.isArray(item.ikhtisars)) {
        return 0
      }

      const total = item.ikhtisars.reduce((total, ikhtisar) => {
        return total + Number(ikhtisar.nominal || 0)
      }, 0)

      return this.normalizeNominal(total)
    },

    // =========================================================
    // SELISIH
    //
    // Nilai Total Panjar - Total Ikhtisar
    // =========================================================
    getSelisih(item) {
      const nilaiPanjar = this.getNilaiTotal(item)
      const nilaiIkhtisar = this.getTotalIkhtisar(item)

      return this.normalizeNominal(nilaiPanjar - nilaiIkhtisar)
    },

    // =========================================================
    // STATUS REKONSILIASI
    // =========================================================
    getStatus(item) {
      const nilaiPanjar = this.getNilaiTotal(item)
      const nilaiIkhtisar = this.getTotalIkhtisar(item)

      if (nilaiIkhtisar <= 0) {
        return 'OPEN'
      }

      if (nilaiIkhtisar >= nilaiPanjar) {
        return 'CLOSED'
      }

      return 'PARTIAL'
    },

    // =========================================================
    // BADGE STATUS
    // =========================================================
    statusBadgeClass(status) {
      if (status === 'CLOSED') {
        return 'badge badge-success'
      }

      if (status === 'PARTIAL') {
        return 'badge badge-warning'
      }

      return 'badge badge-secondary'
    },

    // =========================================================
    // CARI NO. IKHTISAR
    //
    // Hubungan:
    //
    // panjar.ikhtisars[].ikhtisar_hd_id
    //          ↓
    // list ikhtisars[].id
    // =========================================================
    getNoIkhtisar(ikhtisar) {
      if (!ikhtisar) {
        return '-'
      }

      // Kalau object ikhtisar sendiri sudah punya no_ikhtisar
      if (ikhtisar.no_ikhtisar) {
        return ikhtisar.no_ikhtisar
      }

      const headerId = ikhtisar.ikhtisar_hd_id

      const header = this.ikhtisars.find(
        (item) => Number(item.id) === Number(headerId)
      )

      if (header) {
        return header.no_ikhtisar || `IKHTISAR-DOC-${header.id}`
      }

      // Fallback apabila header tidak ditemukan.
      if (headerId) {
        return `DOC-${headerId}`
      }

      return '-'
    },

    // =========================================================
    // DAFTAR DOKUMEN IKHTISAR
    // =========================================================
    getIkhtisarDocuments(item) {
      if (!item || !Array.isArray(item.ikhtisars)) {
        return []
      }

      const documents = []

      item.ikhtisars.forEach((ikhtisar) => {
        const noIkhtisar = this.getNoIkhtisar(ikhtisar)

        if (
          noIkhtisar &&
          noIkhtisar !== '-' &&
          !documents.some(
            (doc) => Number(doc.id) === Number(ikhtisar.ikhtisar_hd_id)
          )
        ) {
          documents.push({
            id: ikhtisar.ikhtisar_hd_id,
            label: noIkhtisar,
          })
        }
      })

      return documents
    },

    // =========================================================
    // TAMPILKAN MAKSIMAL 3 DOKUMEN DI TABEL
    // =========================================================
    getVisibleDocuments(item) {
      return this.getIkhtisarDocuments(item).slice(0, 3)
    },

    // =========================================================
    // JUMLAH DOKUMEN YANG TERSEMBUNYI
    // =========================================================
    getHiddenDocumentCount(item) {
      const documents = this.getIkhtisarDocuments(item)

      return Math.max(documents.length - 3, 0)
    },

    // =========================================================
    // KLIK DOKUMEN
    // =========================================================
    onDocumentClick(id) {
      if (!id) {
        return
      }

      this.$emit('document-click', id)
    },

    // =========================================================
    // CLASS UNTUK SELISIH
    // =========================================================
    selisihClass(item) {
      const selisih = this.getSelisih(item)

      if (selisih === 0) {
        return 'text-success'
      }

      if (selisih > 0) {
        return 'text-danger'
      }

      return 'text-warning'
    },
  },
}
</script>

<template>
  <div class="table-responsive">
    <table class="table table-sm table-hover mb-0">
      <thead>
        <tr class="header-lvl-1">
          <th class="align-middle text-center" style="width: 4%">
            No
          </th>

          <th class="align-middle text-center" style="width: 10%">
            Tanggal Panjar
          </th>

          <th class="align-middle text-left" style="min-width: 280px">
            Uraian
          </th>

          <th class="align-middle text-center" colspan="4">
            Jumlah (Rp)
          </th>

          <th class="align-middle text-center" colspan="2">
            Rekonsiliasi Ikhtisar
          </th>

          <th class="align-middle text-center" style="width: 9%">
            Status
          </th>

          <th class="align-middle text-center" style="min-width: 170px">
            Dokumen Ikhtisar
          </th>
        </tr>

        <tr class="header-lvl-2">
          <th></th>
          <th></th>
          <th></th>

          <th class="text-right">
            Nilai DPP
          </th>

          <th class="text-right">
            PPN
          </th>

          <th class="text-right">
            Biaya Lain
          </th>

          <th class="text-right">
            Nilai Total
          </th>

          <th class="text-right">
            Nilai Ikhtisar
          </th>

          <th class="text-right">
            Selisih
          </th>

          <th></th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <!-- EMPTY -->
        <tr v-if="items.length === 0">
          <td colspan="12" class="text-center py-4 text-muted">
            Belum ada data panjar untuk proyek ini.
          </td>
        </tr>

        <!-- DATA -->
        <tr v-for="(item, index) in items" :key="item.id">
          <!-- NO -->
          <td class="text-center align-middle">
            {{ index + 1 }}
          </td>

          <!-- TANGGAL -->
          <td class="text-center align-middle">
            {{ formatDate(item.tanggal_panjar) }}
          </td>

          <!-- URAIAN -->
          <td class="align-middle">
            <div class="font-weight-bold">
              {{ item.uraian || '-' }}
            </div>

            <small v-if="item.id" class="text-muted">
              Panjar #{{ item.id }}
            </small>
          </td>

          <!-- DPP -->
          <td class="text-right align-middle">
            Rp {{ formatRupiah(getDpp(item)) }}
          </td>

          <!-- PPN -->
          <td class="text-right align-middle">
            Rp {{ formatRupiah(getPpn(item)) }}

            <small v-if="item.ppn" class="d-block text-muted">
              {{ item.ppn }}%
            </small>
          </td>

          <!-- BIAYA LAIN -->
          <td class="text-right align-middle">
            Rp {{ formatRupiah(getBiayaLain(item)) }}
          </td>

          <!-- TOTAL PANJAR -->
          <td class="text-right align-middle font-weight-bold">
            Rp {{ formatRupiah(getNilaiTotal(item)) }}
          </td>

          <!-- TOTAL IKHTISAR -->
          <td
            class="text-right align-middle font-weight-bold"
            :class="getTotalIkhtisar(item) > 0 ? 'text-success' : 'text-muted'"
          >
            Rp {{ formatRupiah(getTotalIkhtisar(item)) }}
          </td>

          <!-- SELISIH -->
          <td
            class="text-right align-middle font-weight-bold"
            :class="selisihClass(item)"
          >
            Rp {{ formatRupiah(getSelisih(item)) }}
          </td>

          <!-- STATUS -->
          <td class="text-center align-middle">
            <span :class="statusBadgeClass(getStatus(item))">
              {{ getStatus(item) }}
            </span>
          </td>

          <!-- DOKUMEN IKHTISAR -->
          <td class="align-middle">
            <div v-if="getVisibleDocuments(item).length" class="document-list">
              <button
                v-for="document in getVisibleDocuments(item)"
                :key="document.id"
                type="button"
                class="btn btn-link btn-sm p-0 mr-2 document-link"
                @click="onDocumentClick(document.id)"
              >
                {{ document.label }}
              </button>

              <span
                v-if="getHiddenDocumentCount(item) > 0"
                class="badge badge-light"
                :title="
                  getIkhtisarDocuments(item)
                    .map((doc) => doc.label)
                    .join(', ')
                "
              >
                +{{ getHiddenDocumentCount(item) }}
              </span>
            </div>

            <span v-else class="text-muted">
              -
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.table-responsive {
  overflow: auto;
  max-height: 600px;
  position: relative;
}

.table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}

.table th,
.table td {
  border: 1px solid #eeeeee;
  padding: 8px 10px;
  background-color: #ffffff;
  white-space: nowrap;
}

.header-lvl-1 th,
.header-lvl-2 th {
  position: sticky;
  background-color: #f8f9fa !important;
  z-index: 20;
  border-bottom: 1px solid #dee2e6;
}

.header-lvl-1 th {
  top: 0;
}

.header-lvl-2 th {
  top: 37px;
  z-index: 19;
  font-size: 12px;
  font-weight: 600;
}

tr:hover td {
  background-color: #f8fafd !important;
}

.document-list {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.document-link {
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.document-link:hover {
  text-decoration: underline;
}
</style>
